import type { MarlinStoreDb } from "../infra/db";
import type { ActiveExecution, ProgressRecord, QuickAction } from "../types";
import { assertValidQuickAction } from "./validate";

export const getQuickActions = async (
  db: MarlinStoreDb,
): Promise<QuickAction[]> => {
  const rows = await db.quickActions.toArray();
  rows.sort(
    (a, b) =>
      (a.createdAt ?? (Number(a.id.replace("qa-", "")) || 0)) -
      (b.createdAt ?? (Number(b.id.replace("qa-", "")) || 0)),
  );
  return rows.map(({ createdAt: _createdAt, ...action }) => action);
};

export const saveQuickAction = (
  db: MarlinStoreDb,
  action: Omit<QuickAction, "id">,
): Promise<QuickAction> =>
  db.transaction("rw", db.quickActions, async () => {
    const maxId = (await db.quickActions.toCollection().primaryKeys()).reduce(
      (max, id) => {
        const n = parseInt(id.replace("qa-", ""), 10);
        return isNaN(n) ? max : Math.max(max, n);
      },
      0,
    );
    const saved: QuickAction = { ...action, id: `qa-${maxId + 1}` };
    assertValidQuickAction(saved);
    const last = await db.quickActions.orderBy("createdAt").last();
    await db.quickActions.add({
      ...saved,
      createdAt: Math.max(Date.now(), (last?.createdAt ?? 0) + 1),
    });
    return saved;
  });

export const updateQuickAction = (
  db: MarlinStoreDb,
  id: string,
  updates: Partial<QuickAction>,
): Promise<void> =>
  db.transaction("rw", db.quickActions, async () => {
    const existing = await db.quickActions.get(id);
    if (!existing) return;
    const next = { ...existing, ...updates, id };
    assertValidQuickAction(next);
    await db.quickActions.put(next);
  });

export const deleteQuickAction = (
  db: MarlinStoreDb,
  id: string,
): Promise<void> =>
  db.transaction("rw", [db.quickActions, db.progress], async () => {
    await db.quickActions.delete(id);
    await db.progress.where("actionId").equals(id).delete();
  });

const groupByAction = (
  rows: ProgressRecord[],
): Record<string, ActiveExecution[]> => {
  const grouped: Record<string, ActiveExecution[]> = {};
  for (const { actionId, ...execution } of rows)
    (grouped[actionId] ??= []).push(execution);
  return grouped;
};

export const getActiveExecutions = async (
  db: MarlinStoreDb,
): Promise<Record<string, ActiveExecution[]>> =>
  groupByAction(
    (await db.progress.toCollection().sortBy("createdAt")).map(
      ({ createdAt: _createdAt, ...execution }) => execution,
    ),
  );

export const createActiveExecution = async (
  db: MarlinStoreDb,
  actionId: string,
  messageId: string,
  machineId: number,
): Promise<void> => {
  await db.transaction("rw", db.progress, async () => {
    if (await db.progress.get([actionId, machineId, messageId])) return;
    const last = await db.progress.orderBy("createdAt").last();
    await db.progress.add({
      actionId,
      machineId,
      messageId,
      createdAt: Math.max(Date.now(), (last?.createdAt ?? 0) + 1),
    });
  });
};

export const updateActiveExecutions = (
  db: MarlinStoreDb,
  machineId: number,
  messageId: string,
  executionStatus: ActiveExecution["executionStatus"],
): Promise<void> =>
  db.transaction("rw", db.progress, async () => {
    const rows = await db.progress
      .where("[machineId+messageId]")
      .equals([machineId, messageId])
      .toArray();
    for (const row of rows) await db.progress.put({ ...row, executionStatus });
  });

export const clearActiveExecution = async (
  db: MarlinStoreDb,
  actionId: string,
): Promise<void> => {
  await db.progress.where("actionId").equals(actionId).delete();
};
